import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Search, Shield, ShieldOff, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';

const SUPER_ADMIN_EMAIL = 'contact.destinestudio@gmail.com';

const ManageUsersModal = ({ onClose }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [canManageAdmins, setCanManageAdmins] = useState(false);

  useEffect(() => {
    loadUsers();
    checkAdminPermissions();
  }, []);

  const checkAdminPermissions = async () => {
    try {
      const response = await axios.get('/api/staff-chat/can-manage-admins');
      setCanManageAdmins(response.data.can_manage);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageAdmins(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminAccess = async (userId, currentStatus) => {
    if (!canManageAdmins) {
      toast.error('Seul le super administrateur peut modifier les accès admin');
      return;
    }
    
    try {
      await axios.post(`/api/admin/users/${userId}/toggle-admin?is_admin=${!currentStatus}`);
      toast.success(currentStatus ? 'Accès admin retiré' : 'Accès admin accordé');
      loadUsers();
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error(error?.response?.data?.detail || 'Erreur');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold">Gérer les Utilisateurs</h2>
              {!canManageAdmins && (
                <p className="text-sm text-yellow-500 mt-1">
                  Seul le super admin peut modifier les accès administrateur
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom ou email..."
              className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-magenta-500"
            />
          </div>
        </div>

        {/* Users List */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Chargement...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-400">Aucun utilisateur trouvé</div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.map((user) => (
                <div
                  key={user.id}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-start justify-between gap-3"
                  data-testid={`admin-user-row-${user.id}`}
                >
                  <div className="flex items-start space-x-4 min-w-0 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center text-lg font-bold overflow-hidden shrink-0">
                      {user.profile_image_url ? (
                        <img src={getMediaUrl(user.profile_image_url)} alt={user.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      ) : (
                        user.name?.charAt(0).toUpperCase() || 'U'
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold truncate">{user.name || 'Sans nom'}</h3>
                      <p className="text-sm text-gray-400 break-all">{user.email}</p>
                      {user.account_type === 'PRO' && user.siret && (
                        <p className="text-xs text-cyan-400 mt-0.5">SIRET: {user.siret}</p>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          user.account_type === 'PRO' 
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {user.account_type}
                        </span>
                        {user.is_admin && (
                          <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400 flex items-center space-x-1">
                            <Shield className="w-3 h-3" />
                            <span>Admin</span>
                          </span>
                        )}
                      </div>
                      {Array.isArray(user.linked_accounts) && user.linked_accounts.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/5" data-testid={`admin-user-linked-${user.id}`}>
                          <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1">
                            <Link2 className="w-3 h-3" /> Utilisateurs associés ({user.linked_accounts.length})
                          </p>
                          <ul className="space-y-1">
                            {user.linked_accounts.map((la) => (
                              <li key={la.id} className="flex items-center gap-2 text-xs text-gray-300" data-testid={`admin-linked-row-${la.id}`}>
                                <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
                                  {la.profile_image_url ? <img src={getMediaUrl(la.profile_image_url)} alt={la.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : (la.name?.charAt(0).toUpperCase() || 'U')}
                                </span>
                                <span className="font-medium truncate">{la.name || la.username || 'Sans nom'}</span>
                                <span className="text-gray-400 break-all">{la.email}</span>
                                {la.account_type && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${la.account_type === 'PRO' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                                    {la.account_type}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Admin Toggle Button - Uniquement visible pour le super admin */}
                  {user.email === SUPER_ADMIN_EMAIL ? (
                    <div className="px-4 py-2 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-medium">
                      Super Admin
                    </div>
                  ) : canManageAdmins ? (
                    <button
                      onClick={() => toggleAdminAccess(user.id, user.is_admin)}
                      className={`px-4 py-2 rounded-full font-medium transition-all flex items-center space-x-2 ${
                        user.is_admin
                          ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                          : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      }`}
                    >
                      {user.is_admin ? (
                        <>
                          <ShieldOff className="w-4 h-4" />
                          <span>Retirer Admin</span>
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4" />
                          <span>Donner Admin</span>
                        </>
                      )}
                    </button>
                  ) : (
                    user.is_admin && (
                      <div className="px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-medium">
                        Administrateur
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageUsersModal;